import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider } from '@flixbus/honeycomb-react';

import UploadCSVModal from '../UploadCSVModal/UploadCSVModal';
import UploadCSVInstructions from '../UploadCSVInstructions/UploadCSVInstructions';
import UploadCSVSubmit from '../UploadCSVSubmit/UploadCSVSubmit';
import UploadCSVFileInput from '../UploadCSVFileInput/UploadCSVFileInput';

import { cancelVouchersByFile } from '../../../apis';
import type { CancelVouchersByCSV } from '../../../types/CancelVouchers';
import { useUploadCSVFormContext } from '../../../context/UploadCSVFormProvider';
import { useNotification } from '../../Notifications/Context';
import { useCountCodes } from '../../../hooks/useCountCodes';

import styles from './upload-csv.module.scss';

const UploadCSVForm: React.FC = () => {
  const {
    formState: { file, reason },
    handleFileChange,
    handleReasonChange,
  } = useUploadCSVFormContext();

  const navigate = useNavigate();
  const { pushToast } = useNotification();
  const { countCodesInCsv } = useCountCodes();

  const [voucherCount, setVoucherCount] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const canConfirm = useMemo(() => !!file && voucherCount > 0, [file, voucherCount]);

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      handleFileChange(f);
      if (f) {
        const count = await countCodesInCsv(f);
        setVoucherCount(count);
      } else {
        setVoucherCount(0);
      }
    },
    [countCodesInCsv, handleFileChange],
  );

  const onRemove = useCallback(() => {
    handleFileChange(null);
    setVoucherCount(0);
    if (inputRef.current) inputRef.current.value = '';
  }, [handleFileChange]);

  const onConfirmClick = useCallback(() => {
    if (!file || voucherCount === 0) return;
    setIsModalOpen(true);
    handleReasonChange('');
  }, [file, voucherCount, handleReasonChange]);

  const submit = useCallback(async () => {
    if (!file || !reason.trim()) return;

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('reason', reason.trim());

      const result: CancelVouchersByCSV = await cancelVouchersByFile(form);
      const id = result.massUpdate?.id;

      navigate(`/upload-csv-success/${id}`);
    } catch (e) {
      let message = 'Unexpected error';
      try {
        message =
          typeof e === 'string' ? JSON.parse(e).message : JSON.parse((e as Error).message).message;
      } catch {
        if (e instanceof Error && e.message) message = e.message;
      }
      pushToast({ message, status: 'danger' });
    } finally {
      setSubmitting(false);
      setIsModalOpen(false);
    }
  }, [file, reason, navigate, pushToast]);

  return (
    <div>
      <section className={styles.container}>
        <UploadCSVInstructions />
        <Divider />

        <UploadCSVFileInput
          file={file}
          onFileChange={onFileChange}
          onRemove={onRemove}
          inputRef={inputRef}
        />
      </section>

      <UploadCSVSubmit canConfirm={canConfirm && !submitting} onConfirm={onConfirmClick} />

      {isModalOpen && (
        <UploadCSVModal
          submitting={submitting}
          reason={reason}
          voucherCount={voucherCount}
          onChangeReason={handleReasonChange}
          onClose={() => setIsModalOpen(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
};

export default UploadCSVForm;
